import { FC, useEffect, useState } from "react";
import close from "assets/images/close.svg";
import Button from "components/particles/primary-button";
import img from "assets/images/input-img-icon.svg";
import imgClose from "assets/images/input-img-close-icon.svg";
import Spinner from "components/particles/loaders/spinner";
import LineLoader from "components/particles/loaders/line-loader";
import FormErrorMessage from "components/particles/forms/form-error-message";

import { useForm } from "react-hook-form";
import { OnBoardingDTO } from "utils/helpers/models/onBoarding.dto";
import { useOnBoarding } from "containers/setting/CMS/onboarding/useHook";
import { Container, ModalContent, StyledReactModal } from "./style";
import { dispatch, RootState } from "store";

import { setModalClosed } from "store/user-slice";
import { useSelector } from "react-redux";

interface OnBoardingModelProps {
  data: any;
  id: number;
  type: string;
  onSuccess: () => void | Promise<void>;
}

const OnBoardingPopup: FC<OnBoardingModelProps> = ({
  data,
  id,
  type,
  onSuccess,
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { postOnBoarding, putOnBoarding } = useOnBoarding();
  const { onBoardingModalIsOpen } = useSelector(
    (state: RootState) => state.user
  );

  const isLoadingData = id !== 0 && !data;
  const isBusy = isSubmitting || isLoadingData;

  const [fileName, setFileName] = useState("");
  const {
    register,
    getValues,
    setValue,
    reset,
    setError,
    clearErrors,
    handleSubmit,
    formState: { errors },
  } = useForm<OnBoardingDTO>();

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedImage(URL.createObjectURL(file));
      setFileName(file.name);
      setValue("image", file, { shouldValidate: true });
      clearErrors("image");
    }
  };

  function closeModal() {
    if (isBusy) return;
    dispatch(setModalClosed("OnBoarding Screen"));
  }

  function closeImg() {
    setSelectedImage(null);
    setFileName("");
    setValue("image", undefined, { shouldValidate: true });
  }

  const onSubmit = async () => {
    if (!selectedImage) {
      setError("image", { type: "required", message: "Image is required" });
      return;
    }

    const formData = new FormData();
    for (let key in getValues()) {
      const value = getValues(key as keyof OnBoardingDTO);
      if (value !== undefined && value !== null && value !== "") {
        formData.append(key, value);
      }
    }
    formData.append("type", type);

    setIsSubmitting(true);
    try {
      if (id === 0) {
        await postOnBoarding(formData);
      } else {
        await putOnBoarding(id, formData);
      }
      await onSuccess();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (data) {
      for (let key in new OnBoardingDTO()) {
        if (data[key] !== undefined) {
          setValue(key as keyof OnBoardingDTO, data[key]);
        }
        if (key === "image") {
          setSelectedImage(data[key]);
        }
      }
      clearErrors();
    } else if (id === 0) {
      reset();
      setSelectedImage(null);
      setFileName("");
      clearErrors();
    }
  }, [data, id, reset, setValue, clearErrors]);

  return (
    <StyledReactModal
      isOpen={onBoardingModalIsOpen}
      onRequestClose={closeModal}
      contentLabel="Onboarding Modal"
    >
      <ModalContent>
        {isBusy && (
          <div className="modal-loader">
            <LineLoader />
          </div>
        )}
        <button
          className="close-button"
          onClick={closeModal}
          disabled={isBusy}
          type="button"
        >
          <img src={close} alt="img" />
        </button>
        <Container onSubmit={handleSubmit(onSubmit)}>
          <div className="model-container">
            <div className="popup-title">
              <h1>{id === 0 ? `Add ${type}` : `Edit ${type} `} </h1>
            </div>
            <div className="add-ingredient-container">
              <div className="image-container">
                <div className="image-input-wrapper">
                  {selectedImage ? (
                    <>
                      <img
                        className="uploaded-image"
                        src={selectedImage}
                        alt="Selected"
                      />
                      <img
                        className="close-icon"
                        src={imgClose}
                        alt="Remove"
                        onClick={closeImg}
                      />
                    </>
                  ) : (
                    <>
                      <label htmlFor="image-input">
                        <img src={img} alt="Upload" />
                      </label>
                      <input
                        id="image-input"
                        type="file"
                        accept="image/*"
                        className="hidden-input"
                        {...register("image", {
                          validate: () =>
                            selectedImage ? true : "Image is required",
                        })}
                        onChange={handleImageChange}
                      />
                    </>
                  )}
                </div>
                <div>
                  <p>{fileName || getValues("image.name")}</p>
                </div>
                <FormErrorMessage error={errors.image} />
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Text 1"
                  {...register("text1", { required: "Text 1 is required" })}
                />
                <FormErrorMessage error={errors.text1} />
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Text 2"
                  {...register("text2", { required: "Text 2 is required" })}
                />
                <FormErrorMessage error={errors.text2} />
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Text 3"
                  {...register("text3", { required: "Text 3 is required" })}
                />
                <FormErrorMessage error={errors.text3} />
              </div>
            </div>

            <div className="add-button">
              <Button
                type="submit"
                title={
                  isSubmitting ? (
                    <Spinner />
                  ) : id === 0 ? (
                    `Add ${type}`
                  ) : (
                    `Update ${type}`
                  )
                }
                width="30rem"
                fill="true"
                disabled={isBusy}
              />
            </div>
          </div>
        </Container>
      </ModalContent>
    </StyledReactModal>
  );
};

export default OnBoardingPopup;
